import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
  writeBatch,
} from "firebase/firestore"
import { db } from "../firebase/config"
import type { Product } from "../types"

interface CartItem extends Product {
  quantity: number;
}

interface WishlistItem {
  id: string;
  name: string;
  price: number;
  image: string;
  category: string;
}

export interface UserProfile {
  id?: string; // Firebase UID
  uid?: string; // Firebase UID (alias for id)
  email: string;
  name: string;
  role: 'user' | 'admin';
  createdAt: string;
  updatedAt?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  phone?: string;
  profilePicture?: string;
  lastLogin?: string;
}

interface SavedAddress {
  id: string;
  name: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  isDefault: boolean;
}

export const userService = {
  // Get all users
  async getAllUsers(): Promise<UserProfile[]> {
    try {
      const usersRef = collection(db, "users")
      const snapshot = await getDocs(usersRef)
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        uid: doc.id, // Ensure uid is also set
        ...doc.data(),
      })) as UserProfile[]
    } catch (error) {
      console.error("Error getting all users:", error)
      throw error
    }
  },

  // Get user data for login purposes
  async getUserData(userId: string): Promise<{ email: string; name: string; role: 'user' | 'admin' } | null> {
    try {
      const userDoc = await getDoc(doc(db, "users", userId))
      if (userDoc.exists()) {
        const data = userDoc.data()
        return {
          email: data.email || '',
          name: data.name || data.displayName || data.email?.split('@')[0] || '',
          role: data.role || 'user', // Default to 'user' if role is missing
        }
      }
      return null
    } catch (error) {
      console.error("Error getting user data:", error)
      return null
    }
  },

  // Get full user profile
  async getUserProfile(uid: string): Promise<UserProfile | null> {
    try {
      const userRef = doc(db, "users", uid)
      const docSnap = await getDoc(userRef)
      if (docSnap.exists()) {
        return { id: docSnap.id, uid: docSnap.id, ...docSnap.data() } as UserProfile
      }
      return null
    } catch (error) {
      console.error("Error getting user profile:", error)
      throw error
    }
  },

  // Update user data
  async updateUserData(userId: string, data: Partial<UserProfile>): Promise<void> {
    try {
      const userRef = doc(db, "users", userId)
      await updateDoc(userRef, {
        ...data,
        updatedAt: new Date().toISOString(),
      })
    } catch (error) {
      console.error("Error updating user data:", error)
      throw error
    }
  },

  // Update cart
  async updateCart(userId: string, cartItems: CartItem[]): Promise<void> {
    try {
      const userRef = doc(db, "users", userId)
      await updateDoc(userRef, {
        cart: cartItems,
        updatedAt: new Date().toISOString(),
      })
    } catch (error) {
      console.error("Error updating cart:", error)
      throw error
    }
  },

  // Update wishlist
  async updateWishlist(userId: string, wishlistItems: WishlistItem[]): Promise<void> {
    try {
      const userRef = doc(db, "users", userId)
      await updateDoc(userRef, {
        wishlist: wishlistItems,
        updatedAt: new Date().toISOString(),
      })
    } catch (error) {
      console.error("Error updating wishlist:", error)
      throw error
    }
  },

  // Initialize user data
  async initializeUserData(userId: string, userData: Omit<UserProfile, 'id' | 'uid'>): Promise<void> {
    try {
      const userRef = doc(db, "users", userId)
      await setDoc(userRef, {
        ...userData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        cart: [],
        wishlist: [],
      })
    } catch (error) {
      console.error("Error initializing user data:", error)
      throw error
    }
  },

  // Create or update user profile
  async updateUserProfile(uid: string, profile: Partial<UserProfile>): Promise<void> {
    try {
      const userRef = doc(db, "users", uid)
      await setDoc(
        userRef,
        {
          ...profile,
          updatedAt: new Date().toISOString(),
        },
        { merge: true },
      )
    } catch (error) {
      console.error("Error updating user profile:", error)
      throw error
    }
  },

  // Get saved addresses
  async getSavedAddresses(userId: string): Promise<SavedAddress[]> {
    try {
      const addressesRef = collection(db, "users", userId, "addresses")
      const snapshot = await getDocs(addressesRef)
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as SavedAddress[]
    } catch (error) {
      console.error("Error getting saved addresses:", error)
      throw error
    }
  },

  // Add saved address
  async addSavedAddress(userId: string, address: Omit<SavedAddress, "id">): Promise<string> {
    try {
      const addressesRef = collection(db, "users", userId, "addresses")

      // If this is set as default, unset other defaults first
      if (address.isDefault) {
        await this.unsetAllDefaultAddresses(userId)
      }

      const docRef = await addDoc(addressesRef, {
        ...address,
        createdAt: new Date().toISOString(),
      })
      return docRef.id
    } catch (error) {
      console.error("Error adding saved address:", error)
      throw error
    }
  },

  // Update saved address
  async updateSavedAddress(userId: string, addressId: string, address: Partial<SavedAddress>): Promise<void> {
    try {
      // If this is set as default, unset other defaults first
      if (address.isDefault) {
        await this.unsetAllDefaultAddresses(userId)
      }

      const addressRef = doc(db, "users", userId, "addresses", addressId)
      await updateDoc(addressRef, {
        ...address,
        updatedAt: new Date().toISOString(),
      })
    } catch (error) {
      console.error("Error updating saved address:", error)
      throw error
    }
  },

  // Delete saved address
  async deleteSavedAddress(userId: string, addressId: string): Promise<void> {
    try {
      const addressRef = doc(db, "users", userId, "addresses", addressId)
      await deleteDoc(addressRef)
    } catch (error) {
      console.error("Error deleting saved address:", error)
      throw error
    }
  },

  // Set default address
  async setDefaultAddress(userId: string, addressId: string): Promise<void> {
    try {
      // First unset all default addresses
      await this.unsetAllDefaultAddresses(userId)

      // Then set the specified address as default
      const addressRef = doc(db, "users", userId, "addresses", addressId)
      await updateDoc(addressRef, {
        isDefault: true,
        updatedAt: new Date().toISOString(),
      })
    } catch (error) {
      console.error("Error setting default address:", error)
      throw error
    }
  },

  // Helper function to unset all default addresses
  async unsetAllDefaultAddresses(userId: string): Promise<void> {
    try {
      const addressesRef = collection(db, "users", userId, "addresses")
      const defaultQuery = query(addressesRef, where("isDefault", "==", true))
      const snapshot = await getDocs(defaultQuery)

      const updatePromises = snapshot.docs.map((doc) => updateDoc(doc.ref, { isDefault: false }))

      await Promise.all(updatePromises)
    } catch (error) {
      console.error("Error unsetting default addresses:", error)
      throw error
    }
  },

  // Create user document on signup
  async createUserDocument(userId: string, userData: UserProfile): Promise<void> {
    try {
      const userRef = doc(db, "users", userId)
      await setDoc(userRef, {
        ...userData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
    } catch (error) {
      console.error("Error creating user document:", error)
      throw error
    }
  },

  // Promote user to admin
  async promoteToAdmin(uid: string): Promise<void> {
    try {
      const userRef = doc(db, "users", uid)
      await updateDoc(userRef, {
        role: "admin",
        updatedAt: new Date().toISOString(),
      })
    } catch (error) {
      console.error("Error promoting user to admin:", error)
      throw error
    }
  },

  // Delete user
  async deleteUser(uid: string): Promise<void> {
    try {
      const userRef = doc(db, "users", uid)
      await deleteDoc(userRef)
    } catch (error) {
      console.error("Error deleting user:", error)
      throw error
    }
  },

  // Merge local and Firestore cart
  async mergeCart(userId: string, localCart: CartItem[]): Promise<CartItem[]> {
    try {
      const userDoc = await getDoc(doc(db, "users", userId));
      let remoteCart: CartItem[] = [];
      if (userDoc.exists() && userDoc.data().cart) {
        remoteCart = userDoc.data().cart;
      }
      // Merge logic: sum quantities for same product, deduplicate
      const merged = [...localCart];
      remoteCart.forEach(remoteItem => {
        const idx = merged.findIndex(item => item.id === remoteItem.id);
        if (idx > -1) {
          merged[idx].quantity += remoteItem.quantity;
        } else {
          merged.push(remoteItem);
        }
      });
      // Atomic update
      const batch = writeBatch(db);
      batch.update(doc(db, "users", userId), { cart: merged, updatedAt: new Date().toISOString() });
      await batch.commit();
      return merged;
    } catch (error) {
      console.error("Error merging cart:", error);
      throw error;
    }
  },

  // Merge local and Firestore wishlist
  async mergeWishlist(userId: string, localWishlist: WishlistItem[]): Promise<WishlistItem[]> {
    try {
      const userDoc = await getDoc(doc(db, "users", userId));
      let remoteWishlist: WishlistItem[] = [];
      if (userDoc.exists() && userDoc.data().wishlist) {
        remoteWishlist = userDoc.data().wishlist;
      }
      // Merge logic: deduplicate by id
      const merged = [...localWishlist];
      remoteWishlist.forEach(remoteItem => {
        if (!merged.find(item => item.id === remoteItem.id)) {
          merged.push(remoteItem);
        }
      });
      // Atomic update
      const batch = writeBatch(db);
      batch.update(doc(db, "users", userId), { wishlist: merged, updatedAt: new Date().toISOString() });
      await batch.commit();
      return merged;
    } catch (error) {
      console.error("Error merging wishlist:", error);
      throw error;
    }
  },

  // Get user by email
  async getUserByEmail(email: string): Promise<UserProfile | null> {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const docSnap = querySnapshot.docs[0];
        return { id: docSnap.id, uid: docSnap.id, ...docSnap.data() } as UserProfile;
      }
      return null;
    } catch (error) {
      console.error('Error getting user by email:', error);
      throw error;
    }
  },

  // Set user role
  async setUserRole(uid: string, role: 'user' | 'admin'): Promise<void> {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, { role, updatedAt: new Date().toISOString() });
    } catch (error) {
      console.error('Error setting user role:', error);
      throw error;
    }
  },
}
